import { useRef, useCallback, useState, useEffect } from "react";
import { setAudioSessionType } from "../lib/audioSession";

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

const SILENCE_RMS_THRESHOLD = 300; // out of 32767; ~-41 dB
const MIN_SPEECH_MS = 400; // must detect speech for this long before silence can trigger
const TARGET_RATE = 16000; // PCM rate the server/Gemini expect

// iOS Safari can silently ignore the requested AudioContext sampleRate when the
// shared hardware audio session is already configured (e.g. by the 24 kHz
// playback context or a route change): the context then runs at 44.1/48 kHz and,
// unguarded, we'd ship wrong-rate PCM labeled 16 kHz — Gemini would hear
// slowed-down garbage. Linear resample per chunk when the rates differ.
function resampleTo16k(samples: Int16Array, fromRate: number): Int16Array {
  if (fromRate === TARGET_RATE) return samples;
  // Averaging decimation, not point-sampling: averaging each output's source
  // window acts as a crude low-pass, so downsampled speech doesn't alias —
  // aliasing is inaudible-ish to humans but measurably degrades ASR.
  const ratio = fromRate / TARGET_RATE;
  const outLen = Math.max(1, Math.floor(samples.length / ratio));
  const out = new Int16Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(samples.length, Math.max(start + 1, Math.floor((i + 1) * ratio)));
    let sum = 0;
    for (let j = start; j < end; j++) sum += samples[j];
    out[i] = sum / (end - start);
  }
  return out;
}

interface AudioWorkletOptions {
  onSilence?: () => void;
  silenceMs?: number;
  gateSpeech?: boolean;
  onTimeout?: () => void;
  preSpeechTimeoutMs?: number;
  // Hard cap on a voiced turn: fires onTimeout if speech keeps running this
  // long without the silence detector closing the turn (safety net for a
  // wedged VAD / runaway mic — the server also caps turns independently).
  maxTurnMs?: number;
}

export function useAudioWorklet(onChunk: (b64: string) => void, { onSilence, silenceMs = 1500, gateSpeech = false, onTimeout, preSpeechTimeoutMs = 10_000, maxTurnMs = 0 }: AudioWorkletOptions = {}) {
  const [isRecording, setIsRecording] = useState(false);
  // True from the tap until the mic pipeline is actually capturing — the UI
  // shows immediate "opening" feedback, but isRecording (the red state) only
  // flips once audio is really flowing.
  const [isArming, setIsArming] = useState(false);
  const contextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onChunkRef = useRef(onChunk);
  const startingRef = useRef(false);
  // Set by stop() while start() is still awaiting getUserMedia/resume, so the
  // in-flight start tears itself down instead of leaving an orphaned mic open.
  const stopRequestedRef = useRef(false);
  const isRecordingRef = useRef(false);
  const moduleLoadedRef = useRef(false);
  // Actual sample rate of the mic AudioContext (null until first start()); iOS
  // can silently override the requested 16 kHz — surfaced for diagnostics.
  const micRateRef = useRef<number | null>(null);
  onChunkRef.current = onChunk;

  const onSilenceRef = useRef(onSilence);
  const silenceMsRef = useRef(silenceMs);
  const gateSpeechRef = useRef(gateSpeech);
  const onTimeoutRef = useRef(onTimeout);
  const preSpeechTimeoutMsRef = useRef(preSpeechTimeoutMs);
  const maxTurnMsRef = useRef(maxTurnMs);
  onSilenceRef.current = onSilence;
  silenceMsRef.current = silenceMs;
  gateSpeechRef.current = gateSpeech;
  onTimeoutRef.current = onTimeout;
  preSpeechTimeoutMsRef.current = preSpeechTimeoutMs;
  maxTurnMsRef.current = maxTurnMs;
  // Per-turn silence detection state — reset on each start() and stop().
  const silenceStartRef = useRef<number | null>(null);
  const speechStartRef = useRef<number | null>(null);
  const silenceFiredRef = useRef(false);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutFiredRef = useRef(false);

  // Buffer audio chunks and flush every ~100 ms so we don't flood the WS
  // with 128-sample (~8 ms) blocks.
  const bufferRef = useRef<Int16Array[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const flushBuffer = useCallback(() => {
    const chunks = bufferRef.current;
    if (chunks.length === 0) return;
    bufferRef.current = [];
    const totalSamples = chunks.reduce((sum, c) => sum + c.length, 0);
    const merged = new Int16Array(totalSamples);
    let offset = 0;
    for (const c of chunks) {
      merged.set(c, offset);
      offset += c.length;
    }
    onChunkRef.current(arrayBufferToBase64(merged.buffer));
  }, []);

  const start = useCallback(async () => {
    if (startingRef.current || isRecordingRef.current) return;
    startingRef.current = true;
    stopRequestedRef.current = false;
    // Immediate tap feedback ("opening the mic") without lying about capture:
    // isRecording flips only below, once the graph is wired and audio is
    // actually flowing, so red strictly means "your words are being heard".
    setIsArming(true);

    try {
      silenceStartRef.current = null;
      speechStartRef.current = null;
      silenceFiredRef.current = false;
      timeoutFiredRef.current = false;
      if (onTimeoutRef.current && preSpeechTimeoutMsRef.current > 0) {
        timeoutTimerRef.current = setTimeout(() => {
          timeoutTimerRef.current = null;
          if (!timeoutFiredRef.current && !speechStartRef.current) {
            timeoutFiredRef.current = true;
            onTimeoutRef.current?.();
          }
        }, preSpeechTimeoutMsRef.current);
      }

      // Recording needs play-and-record; done before getUserMedia so iOS sets
      // up the right session from the start. stop() flips back to "playback"
      // so Elena's replies use the loudspeaker (play-and-record routes to the
      // quiet call receiver).
      setAudioSessionType("play-and-record");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
      if (stopRequestedRef.current) {
        // stop() was tapped while getUserMedia was pending — release everything.
        stream.getTracks().forEach((t) => t.stop());
        setAudioSessionType("playback");
        return;
      }
      streamRef.current = stream;

      // Flip to "recording" here rather than after the graph is wired: once
      // getUserMedia resolves the mic hardware is live, and the remaining
      // context/worklet wiring is tens of ms (the context is cached across
      // turns). Trading that sliver of honesty keeps the arming state short.
      isRecordingRef.current = true;
      setIsRecording(true);
      setIsArming(false);

      // Reuse the AudioContext (and its compiled worklet module) across turns.
      // Rebuilding both per turn added several hundred ms of dead mic time at
      // the start of every turn — the gap where auto mode lost initial words.
      // stop() suspends instead of closing; if the suspended context can't be
      // resumed (iOS can invalidate it between turns), rebuild it.
      let ctx = contextRef.current;
      if (ctx && ctx.state !== "closed") {
        if (ctx.state !== "running") {
          await ctx.resume().catch(() => { /* handled below */ });
        }
        if ((ctx.state as string) !== "running") {
          try { await ctx.close(); } catch { /* already closed */ }
          ctx = null;
        }
      } else {
        ctx = null;
      }
      if (!ctx) {
        moduleLoadedRef.current = false;
        ctx = new AudioContext({ sampleRate: 16000 });
        // Browsers often suspend AudioContext when created after an async call.
        // Explicit resume() is required before the worklet graph will run.
        if (ctx.state === "suspended") {
          await ctx.resume();
        }
      }
      contextRef.current = ctx;
      if (!moduleLoadedRef.current) {
        await ctx.audioWorklet.addModule("/pcm-processor.worklet.js");
        moduleLoadedRef.current = true;
      }
      if (stopRequestedRef.current) {
        // stop() already released the stream (streamRef was set) and reset the
        // session type; just bail before wiring the graph.
        return;
      }
      const workletNode = new AudioWorkletNode(ctx, "pcm-processor");
      workletNodeRef.current = workletNode;

      const contextRate = ctx.sampleRate;
      micRateRef.current = contextRate;
      if (contextRate !== TARGET_RATE) {
        console.warn(`Mic AudioContext running at ${contextRate} Hz (wanted ${TARGET_RATE}); resampling`);
      }
      workletNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
        // A previous turn's node can outlive stop() (its input stays wired
        // until GC) and keep posting silence into the shared buffer,
        // interleaving zeros with live speech: 2x the bytes, half-speed audio
        // that Gemini's ASR hears as another language (session 99).
        if (workletNodeRef.current !== workletNode) return;
        const samples = resampleTo16k(new Int16Array(e.data), contextRate);

        const hasSilenceCb = !!onSilenceRef.current;
        const gating = gateSpeechRef.current;
        if (!hasSilenceCb && !gating) {
          bufferRef.current.push(samples);
          return;
        }

        let sum = 0;
        for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
        const rms = Math.sqrt(sum / samples.length);
        const now = Date.now();
        const isSpeech = rms > SILENCE_RMS_THRESHOLD;

        // Gate only the *leading* edge: drop silence/echo until real speech
        // opens the turn. Once speech has started, forward every frame —
        // including quiet trailing syllables and the trailing silence — so the
        // end of the utterance is never gated out. (Previously sub-threshold
        // trailing words were tagged "silence" and dropped, so they never
        // reached STT; the missing trailing silence also denied Gemini's live
        // ASR the margin it needs to finalize the last segment.)
        if (!gating || isSpeech || speechStartRef.current) {
          bufferRef.current.push(samples);
        }

        if (hasSilenceCb) {
          if (isSpeech) {
            if (!speechStartRef.current) {
              speechStartRef.current = now;
              if (timeoutTimerRef.current) {
                clearTimeout(timeoutTimerRef.current);
                timeoutTimerRef.current = null;
              }
              // Speech has begun, so the pre-speech timeout is spent; replace
              // it with the max-turn cap. If the silence detector never closes
              // the turn (wedged VAD state, RMS pinned high), this still ends
              // it so the session can't freeze on an open mic.
              if (onTimeoutRef.current && maxTurnMsRef.current > 0) {
                timeoutTimerRef.current = setTimeout(() => {
                  timeoutTimerRef.current = null;
                  if (!timeoutFiredRef.current) {
                    timeoutFiredRef.current = true;
                    onTimeoutRef.current?.();
                  }
                }, maxTurnMsRef.current);
              }
            }
            silenceStartRef.current = null;
          } else if (speechStartRef.current && now - speechStartRef.current > MIN_SPEECH_MS) {
            if (silenceStartRef.current === null) silenceStartRef.current = now;
            if (now - silenceStartRef.current >= silenceMsRef.current && !silenceFiredRef.current) {
              silenceFiredRef.current = true;
              onSilenceRef.current?.();
            }
          }
        }
      };

      flushTimerRef.current = setInterval(flushBuffer, 100);

      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      // Route through a silent gain so the worklet stays active without mic feedback
      const silentGain = ctx.createGain();
      silentGain.gain.value = 0;
      source.connect(workletNode);
      workletNode.connect(silentGain);
      silentGain.connect(ctx.destination);
    } catch (err) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      setAudioSessionType("playback");
      isRecordingRef.current = false;
      setIsRecording(false);
      console.error("Failed to start audio worklet:", err);
      throw err;
    } finally {
      startingRef.current = false;
      setIsArming(false);
    }
  }, []);

  const stop = useCallback(() => {
    // If start() is still arming (awaiting getUserMedia / resume), tell it to
    // tear itself down when the pending await resolves.
    if (startingRef.current) stopRequestedRef.current = true;
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    silenceStartRef.current = null;
    speechStartRef.current = null;
    // flush any remaining buffered audio
    flushBuffer();
    // Tear down both ends: workletNode.disconnect() only drops its outputs,
    // so the source -> node edge would keep the old node rendering.
    sourceRef.current?.disconnect();
    sourceRef.current = null;
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
    }
    workletNodeRef.current = null;
    // Suspend rather than close: the context and its loaded worklet module are
    // reused by the next start(), which cuts per-turn mic-arming latency to
    // roughly just getUserMedia. The mic itself is fully released below.
    contextRef.current?.suspend().catch(() => { /* closed or invalidated */ });
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setAudioSessionType("playback");
    isRecordingRef.current = false;
    setIsRecording(false);
    setIsArming(false);
  }, [flushBuffer]);

  // Release the cached AudioContext when the owning component unmounts.
  useEffect(() => () => {
    contextRef.current?.close().catch(() => { /* already closed */ });
    contextRef.current = null;
  }, []);

  return { start, stop, isRecording, isArming, micRateRef };
}
